import { PropsWithChildren, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation } from "react-router";
import {
  autoUpdate,
  offset,
  Placement,
  safePolygon,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions
} from "@floating-ui/react";
import { IconChevronDown, IconMenu2, IconX } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { LangLink, LocaleLink, LocaleNavLink, useLocaleContext, useLocaleNavigate } from "@ct-react/locale";
import { allLocales } from "@shared/locale";
import { useTools } from "../../hooks/tools";
import { useElementSize } from "../../hooks/dom";
import { useMediaQueryDown } from "../../hooks/media-queries";
import { useRoutingTreeContext } from "../../contexts/routing-tree";
import { Wrapper } from "./wrapper";
import SpriteIcon from "../medias/sprite-icon";
import LogoWhite from "../../assets/componable-svgs/agency-logo-white.svg";
import LogoBusiness from "../../assets/componable-svgs/agency-logo-business.svg";
import LogoCompact from "../../assets/componable-svgs/agency-logo-compact.svg";
import "./header.scss";

const transDefs = defineMessages({
  bookmarks: { id: "nav.bookmarks", defaultMessage: "Favoris" }
});

type NavLink = { type: "link", label: string, target: string, order?: number }
type NavSubs = { type: "nav", label: string, children: NavLink[], order?: number }
type NavSelect = { type: "select", label: string, target?: string, children: NavLink[], order?: number }
type Navigation = Array<NavLink | NavSubs | NavSelect>;

type DropdownProps = {
  className?: string;
  label: string;
  target?: string;
  placement?: Placement;
  offset?: number;
  asSelect?: boolean;
}

const DesktopDropdown = (
  {
    className,
    label,
    target,
    placement = "bottom",
    offset: off = 12,
    asSelect = false,
    children
  }: PropsWithChildren<DropdownProps>) => {

  const navigate = useLocaleNavigate();
  const [ open, setOpen ] = useState<boolean>(false);

  const { context, refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: asSelect ? "bottom-end" : placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(asSelect ? 4 : off),
      size({
        apply: ({ rects, elements }) => {
          if (asSelect) {
            elements.floating.style.minWidth = `${rects.reference.width}px`;
            elements.floating.style.boxSizing = "border-box";
          }
        }
      })
    ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { delay: 75, handleClose: safePolygon() }),
    useFocus(context),
    useDismiss(context, { ancestorScroll: true })
  ]);

  const refClasses = classNames(className, { "opened": open, select: asSelect });
  const floatingClasses = classNames("nav-subcontent", { select: asSelect });

  return (
    <>
      <button ref={refs.setReference}
              {...getReferenceProps()}
              type="button"
              {...(!!target) && { onClick: () => navigate(target) }}
              className={refClasses}>
        {label}
        <IconChevronDown className="c-icon" />
      </button>
      {open &&
        <div ref={refs.setFloating}
             {...getFloatingProps()}
             className={floatingClasses}
             style={{...floatingStyles}}>
          {children}
        </div>
      }
    </>);

}

const Header = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const location = useLocation();
  const { nav } = useRoutingTreeContext();

  const { routingData } = useTools();
  const { ref, size } = useElementSize();

  const [ mobileOpen, setMobileOpen ] = useState<boolean>(false);
  const [ minimized, setMinimized ] = useState<boolean>(false);

  const overlap = useMemo<boolean>(() => !!routingData("page")?.headerOverlap, [ routingData ]);

  useLayoutEffect(() => {
    !!size?.height && document.documentElement.style.setProperty("--cgv-header-height", `${size.height}px`);
  }, [ size?.height ])

  useLayoutEffect(() => {
    const handler = () => {
      const should = window.scrollY > 30;
      setMinimized(prev => (prev !== should) ? should : prev);
    }
    document.addEventListener("scroll", handler);
    handler();
    return () => { document.removeEventListener("scroll", handler); };
  }, []);

  const navigationData = useMemo<Navigation>(() => {
    const toNav = (item: any) => {
      const withSubs = "subs" in item;
      return {
        type: withSubs ? (item.subs.every(({ path }: any) => !!path.match(/^\/with-us\//)) ? "select" : "nav") : "link",
        label: (item.label as any)[locale.basename] ?? intl.formatMessage(item.label),
        ...("order" in item) && { order: item.order },
        ...("path" in item) && { target: item.path },
        ...("subs" in item) && { children: item.subs.map(toNav) }
      }
    }
    return (nav || []).map(toNav) as Navigation;
  }, [ nav, intl, locale.basename ]);

  const printNavItem = useCallback((item: NavLink | NavSubs | NavSelect, level: number, mobile: boolean = false) => {

    const parentClasses = classNames({ ellipsed: item.type === "select" });
    const itemClasses = classNames({
      "nav-mitem": !mobile && level === 1,
      "nav-sitem": !mobile && level > 1,
      "nav-m-item": mobile
    });

    if (item.type === "link")
      return (
        <LocaleNavLink to={item.target} end
                       className={itemClasses}
                       {...mobile && { onClick: () => setMobileOpen(false) }}>
          {item.label}
        </LocaleNavLink>);

    else if (mobile || (item.type === "nav" && level > 1))
      return (
        <details className="with-subs">
          <summary className="nav-m-item">
            {item.label}
            <IconChevronDown className="c-icon" />
          </summary>
          <div className="sub-content">
            <ul>
              {item.children.map((child, index) =>
                <li key={index} style={{...!!child.order && { order: child.order }}}>
                  {printNavItem(child, level + 1, mobile)}
                </li>
              )}
            </ul>
          </div>
        </details>);

    else
      return (
        <DesktopDropdown className={itemClasses}
                         label={item.label}
                         asSelect={item.type === "select"}>
          <ul>
            {item.children.map((child, index) =>
              <li key={index} className={parentClasses} style={{...!!child.order && { order: child.order }}}>
                {printNavItem(child, level + 1, mobile)}
              </li>
            )}
          </ul>
        </DesktopDropdown>);

  }, []);

  const untilTablet = useMediaQueryDown("tablet");
  const mobileLandscape = useMediaQueryDown("desktop", { orientation: "landscape" });
  const withBurger = untilTablet || mobileLandscape;
  const wrapperClasses = classNames("main-header", { overlap, minimized, "mobile-opened": mobileOpen });

  return (
    <header ref={ref} className={wrapperClasses}>
      <Wrapper.Centered>

        <div className="brand keep">
          <LocaleLink to={"/"}>
            <h2>
              {(() => {
                if (overlap && !minimized && !mobileOpen)
                  return <LogoWhite />
                if ((overlap && !minimized && mobileOpen) || (!withBurger && !minimized))
                  return <LogoBusiness />
                else
                  return <LogoCompact className="compact" />;
              })()}
            </h2>
          </LocaleLink>
        </div>

        {!withBurger &&
          <>
            <div className="sub-nav">
              <LocaleLink to="/bookmarks/">
                <SpriteIcon hash="action.bookmark" />
              </LocaleLink>
              <DesktopDropdown className="lang"
                               label={locale.basename}
                               placement="left-start"
                               offset={4}>
                <ul>
                  {allLocales.map((l, index) =>
                    <li key={index}
                        className={classNames("label", { active: l.code === locale.code})}>
                      <LangLink locale={l}>{l.label}</LangLink>
                    </li>
                  )}
                </ul>
              </DesktopDropdown>
            </div>
            <nav>
              <ul>
                {navigationData.map((item, index) => (
                  <li key={index}>{printNavItem(item, 1)}</li>)
                )}
              </ul>
            </nav>
          </>
        }

        {withBurger &&
          <>
            <button type="button" className={classNames("burger", { opened: mobileOpen })}
                    onClick={() => setMobileOpen(prev => !prev)}>
              {mobileOpen ? <IconX className="b-icon" /> : <IconMenu2 className="b-icon" />}
            </button>
            {mobileOpen &&
              <nav className="nav-m-overlap">
                <ul>
                  {navigationData.map((item, index) => (
                    <li key={index}>{printNavItem(item, 1, true)}</li>)
                  )}
                </ul>
                <div className="sub-m-nav">
                  <LocaleLink className="nav-m-item bookmarks" to="/bookmarks/" onClick={() => setMobileOpen(false)}>
                    {intl.formatMessage(transDefs.bookmarks)}
                    <SpriteIcon hash="action.bookmark" />
                  </LocaleLink>
                  <details className="lang with-subs">
                    <summary className="nav-m-item">
                      {locale.basename}
                      <IconChevronDown className="c-icon" />
                    </summary>
                    <div className="sub-content">
                      <ul>
                        {allLocales.map((l, index) =>
                          <li key={index}>
                            <LangLink className={classNames("nav-m-item", { active: l.code === locale.code })}
                                      locale={l}
                                      onClick={() => setMobileOpen(false)}
                                      state={location.state}>
                              {l.label}
                            </LangLink>
                          </li>
                        )}
                      </ul>
                    </div>
                  </details>
                </div>
              </nav>
            }
          </>
        }

      </Wrapper.Centered>
    </header>);

}

export default Header;
